import React from 'react';
import { 
  Settings, X, HelpCircle, FileBarChart, AlertTriangle, CheckCircle, Download 
} from 'lucide-react';
import { Button } from './ui/button';

/**
 * Configuration Modal for Dynamic Reports Section
 * Extracted to prevent React re-mounting on parent re-renders
 */
export const DynamicConfigurationModal = ({
  language,
  flowState,
  selectAnswer,
  toggleFinalVersion,
  generateAndPreview,
  resetFlow
}) => {
  if (flowState.step !== 'questions' || !flowState.validationData) return null;
  
  const { data_summary, critical_questions, warnings } = flowState.validationData;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#0A192F] to-[#1a2744] px-6 py-4 flex-shrink-0">
          <h3 className="font-serif text-lg font-bold text-white flex items-center gap-2">
            <Settings className="w-5 h-5 text-gold-400" />
            {language === 'pt' ? 'Configurar Relatório' : 'Configure Report'}
          </h3>
          <p className="text-slate-400 text-xs mt-1">{language === 'pt' ? 'Responda às perguntas para personalizar o output' : 'Answer questions to customize output'}</p>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {/* Data Summary */}
          <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
            <p className="text-xs font-semibold text-slate-500 uppercase mb-3">{language === 'pt' ? 'Dados do Relatório' : 'Report Data'}</p>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="flex justify-between"><span className="text-slate-400">{language === 'pt' ? 'Período:' : 'Period:'}</span><span className="font-semibold text-slate-700">{data_summary?.periodo}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">{language === 'pt' ? 'Receita:' : 'Revenue:'}</span><span className="font-semibold text-slate-700">{data_summary?.receita}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">EBITDA:</span><span className="font-semibold text-slate-700">{data_summary?.ebitda}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">{language === 'pt' ? 'Margem:' : 'Margin:'}</span><span className="font-semibold text-slate-700">{data_summary?.margem_ebitda}</span></div>
            </div>
          </div>
          
          {/* Warnings */}
          {warnings && warnings.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-amber-700 space-y-1">
                  {warnings.map((w, i) => (<p key={i}><strong>{w.message}</strong> — {w.action}</p>))}
                </div>
              </div>
            </div>
          )}
          
          {/* Critical Questions */}
          <div className="space-y-4">
            <p className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <HelpCircle className="w-4 h-4 text-primary" />
              {language === 'pt' ? 'Decisões Críticas' : 'Critical Decisions'}
            </p>
            
            {critical_questions && critical_questions.map((q, idx) => (
              <div key={q.id} className="border border-slate-200 rounded-lg p-4 space-y-3">
                <div className="flex items-start gap-2">
                  <span className="w-5 h-5 bg-primary text-white text-xs font-bold rounded-full flex items-center justify-center flex-shrink-0">{idx + 1}</span>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-700">{q.question}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{q.impact}</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-2 pl-7">
                  {q.options.map((opt) => (
                    <button key={opt.value} onClick={() => selectAnswer(q.id, opt.value)}
                      className={`text-left px-3 py-2.5 rounded-lg border text-sm transition-all ${flowState.answers[q.id] === opt.value ? 'border-primary bg-primary/5 text-primary font-medium' : 'border-slate-200 hover:border-slate-300 text-slate-600'}`}>
                      <div className="flex items-center gap-2">
                        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${flowState.answers[q.id] === opt.value ? 'border-primary' : 'border-slate-300'}`}>
                          {flowState.answers[q.id] === opt.value && <div className="w-2 h-2 bg-primary rounded-full" />}
                        </div>
                        {opt.label}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
          
          {/* Final Version Toggle */}
          <div className="border-t border-slate-200 pt-5">
            <label className="flex items-center gap-3 cursor-pointer">
              <div className={`w-10 h-6 rounded-full transition-colors ${flowState.isFinalVersion ? 'bg-primary' : 'bg-slate-200'}`} onClick={toggleFinalVersion}>
                <div className={`w-4 h-4 bg-white rounded-full shadow-sm transform transition-transform mt-1 ${flowState.isFinalVersion ? 'translate-x-5' : 'translate-x-1'}`} />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-700">{language === 'pt' ? 'Versão Final para Partilha' : 'Final Version for Sharing'}</p>
                <p className="text-xs text-slate-400">{language === 'pt' ? 'Remove marca d\'água DRAFT' : 'Removes DRAFT watermark'}</p>
              </div>
            </label>
          </div>
        </div>
        
        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-200 flex gap-3 flex-shrink-0 bg-slate-50">
          <button 
            type="button"
            onClick={resetFlow} 
            className="flex-1 py-2.5 border border-slate-200 text-slate-600 rounded-lg text-sm font-medium hover:bg-white transition-colors"
          >
            {language === 'pt' ? 'Cancelar' : 'Cancel'}
          </button>
          <button 
            type="button"
            onClick={generateAndPreview} 
            className="flex-1 py-2.5 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors flex items-center justify-center gap-2"
          >
            <FileBarChart className="w-4 h-4" />
            {language === 'pt' ? 'Pré-visualizar' : 'Preview'}
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Configuration Modal for Platform Reports Section
 * Extracted to prevent React re-mounting on parent re-renders
 */
export const PlatformConfigurationModal = ({
  language,
  selectedReport,
  setSelectedReport,
  reportConfig,
  setReportConfig,
  configStep,
  setConfigStep,
  isGenerating,
  setIsGenerating,
  handleGenerateReport,
  handleDownloadReport,
  availableReportsData,
  showHtmlPreview,
  ReportPreviewComponent,
  handleCloseHtmlPreview,
  handleDownloadFromPreview,
  isDownloadingPdf
}) => {
  if (!selectedReport) return null;
  
  const report = availableReportsData.find(r => r.id === selectedReport);
  if (!report) return null;

  // Show HTML Preview instead of the configuration modal when in preview step
  if (showHtmlPreview && ReportPreviewComponent) {
    return (
      <ReportPreviewComponent
        reportId={selectedReport}
        language={language}
        onClose={handleCloseHtmlPreview}
        onDownload={handleDownloadFromPreview}
        isDownloading={isDownloadingPdf}
      />
    );
  }

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      setSelectedReport(null);
      setConfigStep('questions');
      setIsGenerating(false);
    }
  };

  const handleCloseClick = () => {
    setSelectedReport(null);
    setConfigStep('questions');
    setIsGenerating(false);
  };

  const handleCancelClick = () => {
    setSelectedReport(null);
  };

  return (
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" 
      onClick={handleOverlayClick}
    >
      <div 
        className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col" 
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-[#0A192F] to-[#1a2744] px-6 py-4 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Settings className="w-5 h-5 text-gold-400" />
              <h3 className="font-serif text-lg font-bold text-white">
                {language === 'pt' ? 'Configurar Relatório' : 'Configure Report'}
              </h3>
            </div>
            <button 
              type="button"
              onClick={handleCloseClick} 
              className="text-white/70 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content based on step */}
        <div className="p-6 overflow-y-auto flex-1">
          {configStep === 'questions' && (
            <>
              {/* Report Format Selection - FIRST INTERACTION */}
              <div className="mb-6">
                <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                  <FileBarChart className="w-4 h-4 text-primary" />
                  {language === 'pt' ? 'Formato do Relatório' : 'Report Format'}
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  {/* Síntese Executiva - Available */}
                  <button
                    type="button"
                    data-testid="format-compact-10"
                    onClick={() => setReportConfig(prev => ({ ...prev, reportFormat: 'compact_10' }))}
                    className={`p-4 rounded-lg border-2 transition-all text-left ${
                      reportConfig.reportFormat === 'compact_10'
                        ? 'border-primary bg-primary/5'
                        : 'border-slate-200 hover:border-primary/50'
                    }`}
                  >
                    <p className={`text-base font-bold mb-1.5 ${reportConfig.reportFormat === 'compact_10' ? 'text-primary' : 'text-slate-700'}`}>
                      {language === 'pt' ? 'Síntese Executiva' : 'Executive Summary'}
                    </p>
                    <p className="text-xs text-slate-500 leading-relaxed">
                      {language === 'pt' 
                        ? 'Leitura rápida para Conselho de Gerência e stakeholders financeiros' 
                        : 'Quick reading for Board of Directors and financial stakeholders'}
                    </p>
                  </button>
                  {/* Análise Executiva Aprofundada - Disabled in Pilot */}
                  <div
                    data-testid="format-expanded-20"
                    className="p-4 rounded-lg border-2 border-slate-200 bg-slate-50/50 text-left cursor-not-allowed"
                  >
                    <p className="text-base font-bold text-slate-400 mb-1.5">
                      {language === 'pt' ? 'Análise Executiva Aprofundada' : 'In-depth Executive Analysis'}
                    </p>
                    <p className="text-xs text-slate-400 leading-relaxed">
                      {language === 'pt' 
                        ? 'Leitura expandida com maior detalhe analítico e suporte à decisão' 
                        : 'Expanded reading with greater analytical detail and decision support'}
                    </p>
                    <p className="text-[10px] text-slate-400/80 italic mt-2">
                      {language === 'pt' 
                        ? 'Indisponível nesta versão piloto'
                        : 'Not available in pilot version'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Data Summary - Period Selection */}
              <div className="mb-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
                <p className="text-xs text-slate-500 uppercase tracking-wide mb-3">
                  {language === 'pt' ? 'Dados do Relatório' : 'Report Data'}
                </p>
                <p className="text-sm font-medium text-slate-700 mb-3">
                  {language === 'pt' ? 'Período:' : 'Period:'}
                </p>
                <div className="flex flex-wrap gap-2">
                  {[
                    { value: '2026', label: '2026', disabled: true },
                    { value: '4T26', label: '4T26', disabled: true },
                    { value: '3T26', label: '3T26', disabled: true },
                    { value: '2T26', label: '2T26', disabled: true },
                    { value: '1T26', label: '1T26', disabled: false },
                    { value: '2025', label: '2025', disabled: true },
                  ].map((period) => (
                    <button
                      type="button"
                      key={period.value}
                      onClick={() => !period.disabled && setReportConfig(prev => ({
                        ...prev,
                        selectedPeriod: period.value
                      }))}
                      disabled={period.disabled}
                      className={`px-4 py-2 text-sm rounded-lg border transition-all ${
                        period.disabled
                          ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed opacity-60'
                          : reportConfig.selectedPeriod === period.value
                            ? 'bg-primary text-white border-primary'
                            : 'bg-white text-slate-600 border-slate-300 hover:border-primary'
                      }`}
                      title={period.disabled ? (language === 'pt' ? 'Indisponível na versão piloto' : 'Unavailable in pilot version') : ''}
                    >
                      {period.label}
                      {period.disabled && (
                        <span className="ml-1 text-[10px]">🔒</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Critical Questions - Filter out "Nível de detalhe" question */}
              {reportConfig.criticalQuestions.filter(q => q.id !== 'detail_level').length > 0 && (
                <div className="mb-6">
                  <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                    <HelpCircle className="w-4 h-4 text-primary" />
                    {language === 'pt' ? 'Decisões Críticas' : 'Critical Decisions'}
                  </h4>
                  <div className="space-y-4">
                    {reportConfig.criticalQuestions.filter(q => q.id !== 'detail_level').map((q, idx) => (
                      <div key={q.id} className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                        <p className="text-sm font-medium text-slate-700 mb-2">
                          {idx + 1}. {q.question}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {q.options.map((opt) => (
                            <button
                              type="button"
                              key={opt.value}
                              onClick={() => !opt.disabled && setReportConfig(prev => ({
                                ...prev,
                                answers: { ...prev.answers, [q.id]: opt.value }
                              }))}
                              disabled={opt.disabled}
                              className={`px-3 py-1.5 text-xs rounded-full border transition-all ${
                                opt.disabled
                                  ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed opacity-60'
                                  : reportConfig.answers[q.id] === opt.value
                                    ? 'bg-primary text-white border-primary'
                                    : 'bg-white text-slate-600 border-slate-300 hover:border-primary'
                              }`}
                              title={opt.disabled ? opt.disabled_reason : ''}
                            >
                              {opt.label}
                              {opt.disabled && (
                                <span className="ml-1 text-[10px]">🔒</span>
                              )}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Final Version Toggle - Disabled in Pilot Version */}
              <div className="p-4 bg-slate-100 rounded-lg border border-slate-200 opacity-60">
                <label className="flex items-center gap-3 cursor-not-allowed">
                  <input
                    type="checkbox"
                    checked={false}
                    disabled={true}
                    className="w-4 h-4 text-slate-400 border-slate-300 rounded cursor-not-allowed"
                  />
                  <div>
                    <p className="text-sm font-medium text-slate-500">
                      {language === 'pt' ? 'Versão Final para Partilha' : 'Final Version for Sharing'}
                    </p>
                    <p className="text-xs text-slate-400">
                      {language === 'pt' ? 'Remove marca d\'água DRAFT' : 'Removes DRAFT watermark'}
                    </p>
                    <p className="text-[10px] text-slate-400 italic mt-1">
                      {language === 'pt' 
                        ? 'Indisponível nesta versão piloto. Todos os relatórios incluem marca d\'água DRAFT.'
                        : 'Not available in this pilot version. All reports include DRAFT watermark.'}
                    </p>
                  </div>
                </label>
              </div>
            </>
          )}

          {configStep === 'generating' && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4"></div>
              <p className="text-lg font-medium text-slate-700">
                {language === 'pt' ? 'A gerar relatório...' : 'Generating report...'}
              </p>
              <p className="text-sm text-slate-500 mt-1">
                {language === 'pt' ? 'Por favor aguarde' : 'Please wait'}
              </p>
            </div>
          )}

          {configStep === 'preview' && (
            <div className="text-center py-8">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h4 className="text-xl font-semibold text-slate-800 mb-2">
                {language === 'pt' ? 'Relatório Gerado' : 'Report Generated'}
              </h4>
              <p className="text-sm text-slate-500 mb-4">
                {language === 'pt' 
                  ? 'O relatório foi gerado e aberto numa nova aba para revisão.'
                  : 'The report has been generated and opened in a new tab for review.'}
              </p>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-100 text-amber-700 rounded-full text-sm mb-4">
                <AlertTriangle className="w-4 h-4" />
                {language === 'pt' ? 'Versão Draft (com marca d\'água)' : 'Draft Version (with watermark)'}
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex justify-end gap-3 flex-shrink-0">
          {configStep === 'questions' && (
            <>
              <button 
                type="button"
                onClick={handleCancelClick}
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium h-9 px-4 py-2 border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground transition-colors"
              >
                {language === 'pt' ? 'Cancelar' : 'Cancel'}
              </button>
              <button 
                type="button"
                onClick={handleGenerateReport} 
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium h-9 px-4 py-2 bg-primary text-primary-foreground shadow hover:bg-primary/90 transition-colors"
                data-testid="generate-report-btn"
              >
                {language === 'pt' ? 'Pré-visualizar' : 'Preview'}
              </button>
            </>
          )}
          {configStep === 'generating' && (
            <Button 
              type="button"
              variant="outline" 
              onClick={() => {
                setIsGenerating(false);
                setConfigStep('questions');
              }}
            >
              {language === 'pt' ? 'Cancelar' : 'Cancel'}
            </Button>
          )}
          {configStep === 'preview' && (
            <>
              <Button type="button" variant="outline" onClick={() => setConfigStep('questions')}>
                {language === 'pt' ? 'Voltar a Editar' : 'Back to Edit'}
              </Button>
              <Button type="button" onClick={handleDownloadReport} className="bg-primary hover:bg-primary/90">
                <Download className="w-4 h-4 mr-2" />
                {language === 'pt' ? 'Descarregar' : 'Download'}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Generating Modal - Shows loading state
 */
export const GeneratingModal = ({ language, step }) => {
  if (step !== 'generating') return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-xl p-8 flex flex-col items-center gap-4">
        <div className="w-12 h-12 border-4 border-primary/20 border-t-primary rounded-full animate-spin" />
        <div className="text-center">
          <p className="font-semibold text-slate-700">{language === 'pt' ? 'A gerar relatório...' : 'Generating report...'}</p>
          <p className="text-sm text-slate-400 mt-1">{language === 'pt' ? 'Por favor aguarde' : 'Please wait'}</p>
        </div>
      </div>
    </div>
  );
};

/**
 * Preview Modal - Shows success state after PDF generation
 */
export const PreviewModal = ({ 
  language, 
  flowState, 
  backToQuestions, 
  downloadReport 
}) => {
  if (flowState.step !== 'preview' || flowState.showHtmlPreview) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 overflow-hidden">
        <div className="bg-gradient-to-r from-[#0A192F] to-[#1a2744] px-6 py-4">
          <h3 className="font-serif text-lg font-bold text-white flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-emerald-400" />
            {language === 'pt' ? 'Relatório Gerado' : 'Report Generated'}
          </h3>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-slate-600 text-sm">
            {language === 'pt' 
              ? 'O relatório foi gerado e aberto numa nova aba para revisão.' 
              : 'The report was generated and opened in a new tab for review.'}
          </p>
          
          {flowState.isFinalVersion ? (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-500" />
              <span className="text-sm text-emerald-700">{language === 'pt' ? 'Versão Final (sem DRAFT)' : 'Final Version (no DRAFT)'}</span>
            </div>
          ) : (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              <span className="text-sm text-amber-700">{language === 'pt' ? 'Versão Draft (com marca d\'água)' : 'Draft Version (with watermark)'}</span>
            </div>
          )}
          
          <div className="flex gap-3 pt-2">
            <button 
              type="button"
              onClick={backToQuestions} 
              className="flex-1 py-2.5 border border-slate-200 text-slate-600 rounded-lg text-sm font-medium hover:bg-slate-50 transition-colors"
            >
              {language === 'pt' ? 'Voltar a Editar' : 'Edit Again'}
            </button>
            <button 
              type="button"
              onClick={downloadReport} 
              className="flex-1 py-2.5 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors flex items-center justify-center gap-2"
            >
              <Download className="w-4 h-4" />
              {language === 'pt' ? 'Descarregar' : 'Download'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
